import { useQuery, useLazyQuery } from "@apollo/client";
import {
  GENERATE_INVOICE,
  GET_ADMIN_USERS,
  GET_CUSTOMER_BY_ID,
  GET_CUSTOMERS,
  GET_CUSTOMER_PROJECTS,
  GET_INSURANCE_COMPANIES,
  GET_INSURANCE_COMPANY_BY_ID,
  GET_INVOICES,
  GET_MATERIALS_BY_CATEGORY,
  GET_PROJECT_BY_ID,
  GET_PROJECT_DETAILS,
  GET_PROJECT_STAGES,
  GET_PROJECTS,
  GET_SLACK_CHANNELS,
  GET_UPLOAD_URL,
  GET_WORKFLOWS,
  LIST_PRODUCT_SYSTEMS,
} from "../../operation/querys";
import {
  AdminUsersResponse,
  getUploadUrlResponse,
  InsuranceCompaniesResponse,
  InsuranceCompany,
  ListProductSystemsResponse,
  ProjectResponse,
  ProjectsResponse,
  SlackChannelsResponse,
  WorkflowsResponse,
} from "../../../types/graphQlQuery.interface";
import {
  GetCustomerByIDResponse,
  GetCustomerResponse,
} from "../../../redux/types/Project";
import { CustomerByIdResponse } from "../../../redux/types/customers";
import {
  InvoiceResponse,
  ProjectDetailsQueryData,
  ProjectDetailsQueryVars,
} from "../../../redux/types/projectDetails";
import {
  MaterialCategoryQueryVars,
  MaterialResponse,
} from "../../../redux/types/Material";

interface queryInput {
  isSkip?: boolean;
  limit?: number;
  offset?: number;
  status?: string;
  reason?: string;
}

export const useFetchImageUrl = ({ isSkip }: queryInput) => {
  const { loading, error, data, refetch } = useQuery<getUploadUrlResponse>(
    GET_UPLOAD_URL,
    {
      skip: isSkip,
    }
  );

  return {
    loading,
    error,
    uploadUrl: data?.getUploadUrl?.url,
    refetch,
  };
};

export const useFetchCustomers = ({
  limit,
  offset,
  isSkip = false,
}: queryInput) => {
  const { loading, error, data, refetch } = useQuery<
    GetCustomerResponse,
    queryInput
  >(GET_CUSTOMERS, {
    variables: { limit, offset },
    skip: isSkip,
  });

  return {
    loading,
    error,
    customersData: data?.customers,
    refetch,
  };
};

export const useFetchCustomerByID = ({
  limit,
  offset,
  isSkip = false,
}: queryInput) => {
  const { loading, error, data, refetch } = useQuery<
    GetCustomerByIDResponse,
    queryInput
  >(GET_CUSTOMERS, {
    variables: { limit, offset },
    skip: isSkip,
  });

  return {
    loading,
    error,
    customerData: data?.customer,
    refetch,
  };
};

export const useFetchProjects = (
  limit: number = 100,
  offset: number = 0,
  status?: string,
  reason?: string,
  id?: number,
  completed: boolean = true
) => {
  const { loading, error, data, refetch } = useQuery<ProjectsResponse>(
    GET_PROJECTS,
    {
      variables: {
        limit,
        offset,
        status,
        reason,
        id,
        completed,
      },
    }
  );

  return {
    loading,
    error,
    projects: data?.projects,
    refetch,
  };
};

export const useProjectById = (id: number | string) => {
  const { loading, error, data, refetch } = useQuery<
    ProjectResponse,
    { id: number }
  >(GET_PROJECT_BY_ID, {
    variables: { id: Number(id) },
  });
  return {
    loading,
    error,
    project: data?.project,
    refetch,
  };
};

export const useCustomerById = ({
  id,
  isSkip = false,
}: {
  id: number;
  isSkip?: boolean;
}) => {
  const { data, loading, error, refetch } = useQuery<
    CustomerByIdResponse,
    { id: number }
  >(GET_CUSTOMER_BY_ID, {
    variables: { id },
    skip: isSkip,
  });

  return {
    customer: data?.customer,
    loading,
    error,
    refetch,
  };
};

export const useFetchCustomerProjects = ({
  customerId,
  isSkip = false,
}: {
  customerId: number;
  isSkip?: boolean;
}) => {
  const { data, loading, error, refetch } = useQuery<
    { getCustomerProjects: any[] },
    { input: { customerId: number } }
  >(GET_CUSTOMER_PROJECTS, {
    variables: { input: { customerId } },
    skip: isSkip,
  });

  return {
    customerProjects: data?.getCustomerProjects,
    loading,
    error,
    refetch,
  };
};

export const useFetchProjectDetails = ({
  projectId,
}: {
  projectId: number;
  isSkip?: boolean;
}) => {
  const { data, loading, error, refetch } = useQuery<
    ProjectDetailsQueryData,
    ProjectDetailsQueryVars
  >(GET_PROJECT_DETAILS, { variables: { projectId }, fetchPolicy: "no-cache" });

  return {
    projectDetails: data?.projectDetails,
    loading,
    error,
    refetch,
  };
};

export const useFetchInvoices = ({
  projectId,
}: {
  projectId: number;
  isSkip?: boolean;
}) => {
  const { data, loading, error, refetch } = useQuery<
    InvoiceResponse,
    ProjectDetailsQueryVars
  >(GET_INVOICES, { variables: { projectId }, fetchPolicy: "no-cache" });

  return {
    invoiceDetails: data?.invoices,
    loading,
    error,
    refetch,
  };
};

export const useMaterialsByCategory = (categoryId: number) => {
  const { data, loading, error } = useQuery<
    MaterialResponse,
    MaterialCategoryQueryVars
  >(GET_MATERIALS_BY_CATEGORY, {
    variables: { id: categoryId },
  });

  return {
    materials: data?.materialsByCategory,
    loading,
    error,
  };
};

interface PreviewInvoiceResponse {
  previewInvoice: {
    url: string;
    additionalUrls: string[];
  };
}

export const usePreviewInvoice = () => {
  const [fetchPreviewInvoice, { loading, error, data }] = useLazyQuery<
    PreviewInvoiceResponse,
    { projectId: number; source?: string }
  >(GENERATE_INVOICE);

  return {
    generateInvoice: fetchPreviewInvoice,
    loading,
    error,
    previewUrl: data?.previewInvoice?.url,
  };
};


export const useFetchProductSytems = () => {
  const { loading, error, data, refetch } = useQuery<ListProductSystemsResponse>(LIST_PRODUCT_SYSTEMS);

  return {
    loading,
    error,
    productSystems: data?.listProductSystems,
    refetch,
  };
}


export const useFetchInsuranceCompanies = ({ limit, offset }: queryInput) => {
  const { data, loading, error, refetch } =
    useQuery<InsuranceCompaniesResponse>(GET_INSURANCE_COMPANIES, {
      variables: { limit, offset },
    });

  return {
    insuranceCompanies: data?.insuranceCompanies || [],
    loading,
    error,
    refetch,
  };
};

export const useFetchInsuranceCompanyById = (id: number, skip?: boolean) => {
  const { data, loading, error, refetch } = useQuery<{insuranceCompany: InsuranceCompany}>(
    GET_INSURANCE_COMPANY_BY_ID,
    {
      variables: { id },
      skip: skip || !id,
    }
  );

  return {
    insuranceCompany: data?.insuranceCompany || null,
    loading,
    error,
    refetch,
  };
};

export const useFetchSlackChannels = (skip?: boolean) => {
  const { data, loading, error, refetch } = useQuery<SlackChannelsResponse>(
    GET_SLACK_CHANNELS,
    {
      skip,
    }
  );

  return {
    slackChannels: data?.slackChannels || [],
    loading,
    error,
    refetch,
  };
};

export const useFetchWorkflows = (skip?: boolean) => {
  const { data, loading, error, refetch } = useQuery<WorkflowsResponse>(
    GET_WORKFLOWS,
    {
      skip,
      fetchPolicy: "no-cache",
    }
  );

  return {
    workflows: data?.getWorkflows || [],
    loading,
    error,
    refetch,
  };
};

export const useFetchAdminUsers = (limit: number = 100, offset: number = 0, skip?: boolean) => {
  const { data, loading, error, refetch } = useQuery<AdminUsersResponse>(
    GET_ADMIN_USERS,
    {
      variables: { limit, offset },
      skip,
      fetchPolicy: "no-cache",
    }
  );

  return {
    adminUsers: data?.adminUsers || [],
    adminEmails: data?.adminUsers?.map(user => user.email) || [],
    loading,
    error,
    refetch,
  };
};

export interface ProjectStageResponse {
  name: string;
  completed: boolean;
  completionPercentage: number | null;
  subSections: ProjectStageResponse[] | null;
}

export const useFetchProjectStages = ({
  projectId,
  skip = false,
}: {
  projectId: number;
  skip?: boolean;
}) => {
  const { data, loading, error, refetch } = useQuery<
    { GetProjectStages: ProjectStageResponse[] },
    { projectId: number }
  >(GET_PROJECT_STAGES, {
    variables: { projectId },
    fetchPolicy: "cache-and-network",
    skip: skip || !projectId,
    notifyOnNetworkStatusChange: false,
  });

  return {
    stages: data?.GetProjectStages,
    loading,
    error,
    refetch,
  };
};